import React from 'react';
import { Loader2 } from 'lucide-react';
import { CreativeAnalysis, CreativeAnalysisIntentAppeal } from '../../services/api/imageGenerationService';
import { APPEAL_TYPE_LABELS, AppealType } from '../../types/knowledge';

/**
 * 訴求タイプごとの色（/knowledge の訴求カード AppealGenerateCard と同系色を踏襲）。
 */
const APPEAL_TYPE_COLORS: Partial<Record<AppealType, { color: string; bg: string }>> = {
  feature: { color: '#616FC9', bg: 'rgba(97,111,201,.10)' },
  functional: { color: '#616FC9', bg: 'rgba(97,111,201,.10)' },
  merit: { color: '#4F86C6', bg: 'rgba(79,134,198,.10)' },
  benefit: { color: '#44C2A4', bg: 'rgba(68,194,164,.12)' },
  value: { color: '#44C2A4', bg: 'rgba(68,194,164,.12)' },
  trust: { color: '#9675C9', bg: 'rgba(150,117,201,.10)' },
  emotion: { color: '#C9619C', bg: 'rgba(201,97,156,.10)' },
  negative: { color: '#C9A061', bg: 'rgba(201,160,97,.13)' },
  offer: { color: '#E0894B', bg: 'rgba(224,137,75,.13)' },
  comparison: { color: '#5BA8C4', bg: 'rgba(91,168,196,.12)' },
  social: { color: '#6FA86F', bg: 'rgba(111,168,111,.13)' },
};
const DEFAULT_APPEAL_TYPE_COLOR = { color: '#616FC9', bg: 'rgba(97,111,201,.10)' };
// 訴求軸バッジの色（/knowledge の GOLD バッジを踏襲）
const APPEAL_AXIS_COLOR = { color: '#A87C2B', bg: 'rgba(201,160,97,.14)' };

/**
 * 小さめの「AI分析実行中」インジケータ。控えめなグレー文字＋小スピナー。
 * バナー詳細の AI効果予測、カルーセル詳細の制作意図の両方の読み込み中表示に使う。
 */
export const CreativeAnalysisLoadingIndicator: React.FC<{ label?: string }> = ({
  label = 'AIが効果予測を実行中です...',
}) => (
  <div className="flex items-center gap-2 py-2 text-xs text-gray-400" role="status">
    <Loader2 className="w-3.5 h-3.5 animate-spin" />
    {label}
  </div>
);

/**
 * 「使用スタイル」の1行。制作意図セクション内と、分析の読み込み中/失敗時に
 * インラインフレームを描く BannerDetailPanel の分岐で共用する。
 * スタイル名は generated_images に非正規化保存された値（GeneratedImage.style_name）で、
 * AI分析（creative-analysis）の成否に依存せず表示できる。styleName が無ければ何も描かない。
 */
export const CreativeIntentStyleRow: React.FC<{ styleName?: string | null }> = ({ styleName }) => {
  if (!styleName) return null;
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-1">
        <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full" />
        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
          使用スタイル
        </span>
      </div>
      <p className="text-sm text-gray-700 pl-3 leading-relaxed">{styleName}</p>
    </div>
  );
};

/**
 * バナー詳細・カルーセル詳細で共用する「制作意図」表示ブロック。
 *
 * 新形式（DCM実データ連携後）は intent.appeal がオブジェクト、旧形式は文字列。
 * 旧データはそのまま従来のレイアウト（サマリ/セグメント/訴求）で表示する。
 *
 * styleName は AI分析とは独立した非正規化データのため、intent が無い（＝分析なし/未実行）
 * バナーでも styleName があれば「使用スタイル」行だけを描画する。両方無ければ null。
 */
export const CreativeIntentSection: React.FC<{
  analysis: CreativeAnalysis | null;
  styleName?: string | null;
}> = ({ analysis, styleName }) => {
  const intent = analysis?.intent ?? null;

  // 制作意図（analysis）もスタイル名も無ければセクションごと描画しない。
  if (!intent && !styleName) return null;

  const isNewIntentFormat = !!intent && typeof intent.appeal === 'object' && intent.appeal !== null;
  const legacyAppeal = intent && typeof intent.appeal === 'string' ? intent.appeal : '';
  const intentAppeal = intent && isNewIntentFormat ? (intent.appeal as CreativeAnalysisIntentAppeal) : null;
  const appealTypeColor = intentAppeal?.type
    ? APPEAL_TYPE_COLORS[intentAppeal.type as AppealType] ?? DEFAULT_APPEAL_TYPE_COLOR
    : DEFAULT_APPEAL_TYPE_COLOR;

  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <div className="flex items-center gap-2 mb-4">
        <span className="text-base">&#x1F4CD;</span>
        <span className="text-sm font-semibold text-gray-900">制作意図</span>
      </div>

      <div className="space-y-3">
        <CreativeIntentStyleRow styleName={styleName} />

        {!isNewIntentFormat && intent?.summary && (
          <div>
            <div className="flex items-center gap-1.5 mb-1">
              <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full" />
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                サマリ
              </span>
            </div>
            <p className="text-sm text-gray-700 pl-3 leading-relaxed">
              {intent?.summary}
            </p>
          </div>
        )}

        {intent?.segment && (
          <div>
            <div className="flex items-center gap-1.5 mb-1">
              <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full" />
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                セグメント
              </span>
            </div>
            <p className="text-sm text-gray-700 pl-3 leading-relaxed">
              {intent?.segment}
            </p>
          </div>
        )}

        {isNewIntentFormat && intent?.insight && (
          <div>
            <div className="flex items-center gap-1.5 mb-1">
              <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full" />
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                インサイト
              </span>
            </div>
            <p className="text-sm text-gray-700 pl-3 leading-relaxed">
              {intent?.insight}
            </p>
          </div>
        )}

        {!isNewIntentFormat && legacyAppeal && (
          <div>
            <div className="flex items-center gap-1.5 mb-1">
              <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full" />
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                訴求
              </span>
            </div>
            <p className="text-sm text-gray-700 pl-3 leading-relaxed">
              {legacyAppeal}
            </p>
          </div>
        )}

        {isNewIntentFormat && intentAppeal && (intentAppeal.type || intentAppeal.appeal_axis || intentAppeal.name) && (
          <div>
            <div className="flex items-center gap-1.5 mb-1">
              <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full" />
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                訴求
              </span>
            </div>
            <div className="pl-3">
              <div className="flex flex-wrap items-center gap-1.5 mb-1">
                {intentAppeal.type && (
                  <span
                    className="rounded-full px-2 py-0.5 text-[10px] font-semibold"
                    style={{ color: appealTypeColor.color, backgroundColor: appealTypeColor.bg }}
                  >
                    {APPEAL_TYPE_LABELS[intentAppeal.type as AppealType] ?? intentAppeal.type}
                  </span>
                )}
                {intentAppeal.appeal_axis && (
                  <span
                    className="rounded-full px-2 py-0.5 text-[10px] font-bold"
                    style={{ color: APPEAL_AXIS_COLOR.color, backgroundColor: APPEAL_AXIS_COLOR.bg }}
                  >
                    {intentAppeal.appeal_axis}
                  </span>
                )}
              </div>
              {intentAppeal.name && (
                <p className="text-sm font-semibold text-gray-800 leading-relaxed">{intentAppeal.name}</p>
              )}
            </div>
          </div>
        )}

        {isNewIntentFormat && intent?.creative_intent && (
          <div>
            <div className="flex items-center gap-1.5 mb-1">
              <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full" />
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                制作意図
              </span>
            </div>
            <p className="text-sm text-gray-700 pl-3 leading-relaxed">
              {intent?.creative_intent}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreativeIntentSection;
